import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, CheckCircle2, ShieldCheck } from "lucide-react";

/**
 * In-place change-password form for the *logged-in* user.
 * Requires the current password; no email/token round-trip needed.
 * On success the JWT is invalidated server-side (SEC-021), so we sign the
 * user out and send them back to /login to authenticate with the new password.
 */
export default function ChangePassword() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirm) {
      setError("New password and confirmation do not match.");
      return;
    }
    if (newPassword === currentPassword) {
      setError("New password must be different from your current password.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setDone(true);
      // Server has invalidated the JWT; sign the user out locally, then
      // bounce back to /login after a short confirmation moment.
      setTimeout(async () => {
        try { await logout(); } catch (_) { /* ignore — server already killed the session */ }
        navigate("/login", { replace: true });
      }, 1800);
    } catch (err) {
      setError(formatApiErrorDetail(err) || "Could not update password.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 px-6">
        <div className="max-w-md w-full text-center space-y-4 bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
          <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
          <h1 className="text-xl font-semibold text-slate-900">Password updated</h1>
          <p className="text-sm text-slate-600">
            Signing you out — please log back in with your new password.
          </p>
          <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 px-6 py-10">
      <div className="max-w-md w-full space-y-5" data-testid="change-password-page">
        <Link
          to="/dashboard"
          className="text-xs inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-800"
          data-testid="change-password-back"
        >
          <ArrowLeft className="h-3 w-3" /> Back to dashboard
        </Link>

        <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm space-y-5">
          <div className="space-y-1.5">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500">
              <ShieldCheck className="h-3 w-3" /> Account security
            </div>
            <h1 className="text-2xl font-semibold text-slate-900">Change password</h1>
            <p className="text-sm text-slate-600">
              Signed in as <span className="font-medium text-slate-800">{user?.email}</span>.
              You'll be signed out after the change and asked to log back in.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="current-password">Current password</Label>
              <Input
                id="current-password"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                data-testid="change-password-current-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="new-password">New password</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                data-testid="change-password-new-input"
                className="mt-1.5"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Minimum 8 characters. Mix upper &amp; lower case, numbers and a symbol for best security.
              </p>
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm new password</Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={8}
                data-testid="change-password-confirm-input"
                className="mt-1.5"
              />
            </div>

            {error && (
              <div
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                data-testid="change-password-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || !currentPassword || !newPassword || !confirm}
              className="w-full"
              data-testid="change-password-submit"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
            </Button>
          </form>

          <p className="text-[11px] text-slate-500 text-center pt-2 border-t border-slate-100">
            Forgot your current password?{" "}
            <Link
              to="/forgot-password"
              className="text-blue-600 underline underline-offset-2 hover:text-blue-700"
              data-testid="change-password-forgot-link"
            >
              Use the reset flow
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
